import { Injectable } from '@angular/core';
import { Observable, Observer, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig } from '../../utils/app-config.service';
import { AdvisoryModel, ClientSignatureType, IdInterface, OrderSignedInterface, TestInterface, TestResultInterface } from '../../../core';
import { LocalStorage } from '../helpers/local-storage';
import { MyHttpClient } from './my-http-client';
import { Test, TestResult } from '../../domain/data';
import { Serializer } from '../helpers/serializer/serializer';

import { mapCommonResponse, mapOrderSigned } from './api-client.helpers';

import { environment } from '../../../../environments/environment';

@Injectable()
export class TestApiClient {
    constructor(private http: MyHttpClient, private localStorage: LocalStorage, private serializer: Serializer) {}

    acceptTestSignSignatures(clientCode: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.accept_test_sign_signatures).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ClientCode: String(clientCode),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    continueTestElectronicSignature(clientCode: IdInterface): Observable<OrderSignedInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.continue_test_electronic_signature).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ClientCode: String(clientCode),
                    })
                    .pipe(map((resp: any) => mapOrderSigned(resp)));
            })
        );
    }

    getTest(clientId: IdInterface, advisoryModel: AdvisoryModel): Observable<TestInterface> {
        return new Observable((observer: Observer<string>) => {
            let url = AppConfig.settings.services.base + environment.services.paths.get_test;

            if (advisoryModel === 'A' || advisoryModel === 'P' || advisoryModel === 'G') {
                url = AppConfig.settings.services.base + environment.services.paths.get_test_suitability;
            }

            observer.next(url);
            observer.complete();
        }).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ClientId: String(clientId),
                        UseShortTexts: false,
                        AdvisoryModelCode: String(advisoryModel),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJson(resp, Test)));
            })
        );
    }

    getTestResults(clientId: IdInterface, advisoryModel: AdvisoryModel): Observable<TestResultInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_client_profile).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        Id: String(clientId),
                    })
                    .pipe(
                        switchMap((resp: any) => {
                            if (resp && resp.ProfileTestId) {
                                return of(AppConfig.settings.services.base + environment.services.paths.get_test_results).pipe(
                                    switchMap((url2) => {
                                        return this.http
                                            .post(url2, {
                                                TestId: resp.ProfileTestId,
                                                ClientId: String(clientId),
                                                UseShortTexts: false,
                                                AdvisoryModelCode: String(advisoryModel),
                                            })
                                            .pipe(map((resp2: any) => this.serializer.fromJson(resp2, TestResult)));
                                    })
                                );
                            } else {
                                return of(new TestResult());
                            }
                        })
                    );
            })
        );
    }

    signTest(clientCode: IdInterface, signatureType: ClientSignatureType): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.sign_test).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ClientCode: String(clientCode),
                        SignatureType: String(signatureType),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    updateTest(clientId: IdInterface, test: TestInterface, requiredSignature = false): Observable<TestResultInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.set_test).pipe(
            switchMap((url) => {
                const answers = [];

                test.questions.map((question) => {
                    const answer = {
                        Id: String(question.id),
                        Options: [],
                    };

                    if (question.groups && question.groups.length > 0) {
                        question.groups.map((group) => {
                            group.options.map((option) => {
                                if (option.selected) {
                                    answer.Options.push({
                                        Id: String(option.id),
                                    });
                                }
                            });
                        });
                    } else {
                        question.options.map((option) => {
                            if (option.selected) {
                                answer.Options.push({
                                    Id: String(option.id),
                                });
                            }
                        });
                    }

                    answers.push(answer);
                });

                return this.http
                    .post(url, {
                        AdvisoryModelCode: test.advisoryModel,
                        QuestionnaireId: String(test.id),
                        ClientId: String(clientId),
                        RequiredSignature: requiredSignature,
                        Answers: answers,
                    })
                    .pipe(
                        map((resp: any) => {
                            if (!resp || (resp.Status !== 'OK' && resp.Status !== 'ok' && resp.Status !== '')) {
                                throw new Error(resp ? resp.Status : 'error.general');
                            }

                            return this.serializer.fromJson(resp.Result || {}, TestResult);
                        })
                    );
            })
        );
    }
}
